// MatterWitnessesCard — per-matter CCP §2034 witness disclosure roster.
// Ship 7i.1 frontend admin REDO (2026-08-26).
//
// Panel-flagged CRITs + HIGHs folded — verify checkpoints below the
// Ship 7i one-liner are what this file must satisfy or the parent will
// roll back the ship:
//
//   (Backend CRIT-1 shipped as backend contract)  UNIQUE(matter_id, contact_id)
//   dropped; partial UNIQUE WHERE superseded_at IS NULL + prior_row_id chain
//   preserved.  Reclassification is a supersession event on the backend —
//   the frontend NEVER pretends it's an in-place UPDATE.
//
//   (Backend HIGH-6 shipped as backend contract)  §2034.260(c) 5-field
//   completeness enforced at write time.  Frontend mirrors the validator
//   client-side (validateRetainedExpertCompleteness / …NonRetained…) so
//   the ceremony fires PRE-modal-open — per Frontend HIGH-2 from the
//   pre-code panel (concurrent-view 3/3): a client-side gate BEFORE the
//   §2034.270 confirm modal keeps the wasted-ceremony class of UX bug
//   from happening.  A 400 from the backend on completeness renders
//   per-field red errors + preserves entered data.
//
//   (Frontend HIGH-1, pre-code panel 3/3 convergence)  409 stale-record
//   from the supersession endpoint is a user-visible red banner:
//   "This witness was reclassified by <name> at <time>.  Reload to see
//   the current designation."  We refetch on 409 + block further edits
//   until the row is refreshed.  Cancel-in-modal reverts the visible
//   witness_type control to server-state, not the transient in-modal
//   choice (double-submit prevention via `busy` guard).
//
//   (Frontend HIGH-3, pre-code panel 3/3 convergence)  matter.designating_party
//   is not managed here — it's on MatterDetail's matter-info section
//   because the picker is a matter-level attribute, not a per-witness one.
//   The Expert Designation PDF button consumes it.
//
//   Design-contract fold:
//     * Witness list shows CURRENT rows by default; toggle "Show superseded"
//       reveals the full chain (grey strikethrough + amber "Superseded on"
//       chip).  A "Restore" action for superseded rows was described here as
//       though it shipped; it never did — grep the file, the only match is
//       this comment, and CLAUDE.md lists it as deferred to Ship 7i.2.
//       Corrected 2026-09-16 rather than left standing, because it reads as a
//       precedent for un-doing a row state and the withdrawal work below
//       deliberately has no such action.
//     * Toggle "Show withdrawn" is the SEPARATE axis (rose "WITHDRAWN on"
//       chip).  A designation can be replaced, pulled, both, or neither, so a
//       row can carry both chips.
//     * "Change witness type" is a dedicated per-row button (NOT a silent
//       dropdown in the edit modal).  Opens ReclassifyWitnessModal directly.
//     * Add + Edit modals — retained-expert form fields show required-
//       asterisks on all 5 §2034.260(c) fields + subject_of_testimony.
//       Client-side pre-validation.  Server 400 → per-field red errors.
//     * "Record Disclosure" per-row button — modal for designation_round
//       + served_at date + service_method + notes.  Append-only per backend
//       contract (no PUT/DELETE endpoint exists).
//     * Disclosure events render inline as an expandable footer per row.
//
// Distinct from Case Analysis Cast tab:
//   - Cast (theory-of-case work-product): every contact linked to ≥1
//     case_fact_contacts row.  Attorney mental impressions, potentially
//     privileged.  DERIVED from fact links, not authored directly.
//   - Witnesses (formal disclosure roster): the statutory list served on
//     opposing counsel under CCP §2034.210 / §2034.230.  Authored
//     directly.  Distinguishes percipient vs retained expert vs
//     non-retained expert; carries subject_of_testimony, hourly_rate,
//     qualifications, etc.
//
// Original Ship 7i panel-folded designs preserved:
//   (H1 Ship 7i) Coexist model — no auto-sync FROM Cast INTO Witnesses.
//   (H2 Ship 7i) Contact-source scoping — matterContacts first, then
//        opt-in fallback to /api/contacts (backend firm-scopes).
//   (H3 Ship 7i) Reclassification is an EXPLICIT event — now hardened to
//        a dedicated button + supersede/coexist branch, not a passive
//        banner on a silent select.

import { useEffect, useState, useMemo, Fragment } from 'react'
import { useAsk } from '../context/PromptProvider'
import { useModal } from '../hooks/useModal'
import useFieldIds from '../hooks/useFieldIds'
import EmptyState from './EmptyState'
// Ship 7i.2 CB-4 — shared override→contact precedence resolver.
import { witnessDisplayName, resolveWitnessIdentity } from '../lib/witnessIdentity'
import { CONTACT_GROUPS, groupLabel } from '../lib/contactGroups'
import api, {
  // Ship 7i coexist model — READ-ONLY cross-link into Case Analysis.  The
  // Witnesses roster never writes to the Cast and the Cast never writes to
  // the roster; this is only used to mark rows that appear in both.
  getCaseCast,
  listMatterWitnesses,
  createMatterWitness,
  updateMatterWitness,
  deleteMatterWitness,
  withdrawMatterWitness,
  listWitnessDisclosureEvents,
  createWitnessDisclosureEvent,
} from '../services/api'

// Canonical CCP §2034 witness classifications.  Frontend enum mirrors
// backend CHECK constraint — keep in sync.
const WITNESS_TYPES = [
  { value: 'fact',                 label: 'Fact',                 chip: 'bg-sky-100 text-sky-800',
    desc: 'Percipient witness with knowledge of underlying events (non-expert testimony).' },
  { value: 'percipient',           label: 'Percipient',           chip: 'bg-blue-100 text-blue-800',
    desc: 'Witness with direct sensory knowledge of relevant events.' },
  { value: 'expert_retained',      label: 'Expert (retained)',    chip: 'bg-purple-100 text-purple-800',
    desc: 'CCP §2034.210(b) — retained expert.  Requires §2034.260(c) declaration with 5 disclosure fields (specialty + qualifications + subject + deposition + trial rates) + prior-testimony list.' },
  { value: 'expert_non_retained',  label: 'Expert (non-retained)',chip: 'bg-fuchsia-100 text-fuchsia-800',
    desc: 'CCP §2034.210(b) — expert not retained (treating physician, employee expert, etc.).  Requires specialty + subject of testimony.' },
]

const witnessTypeMeta = (v) => WITNESS_TYPES.find(t => t.value === v) || WITNESS_TYPES[0]

const isExpertType = (t) => t === 'expert_retained' || t === 'expert_non_retained'
const isRetainedExpertType = (t) => t === 'expert_retained'

const DESIGNATION_ROUNDS = [
  { value: 'initial',      label: 'Initial designation (§2034.220)' },
  { value: 'supplemental', label: 'Supplemental designation (§2034.280)' },
  { value: 'rebuttal',     label: 'Rebuttal designation (§2034.310)' },
]

const SERVICE_METHODS = [
  { value: 'personal',  label: 'Personal service' },
  { value: 'mail',      label: 'US Mail' },
  { value: 'e-service', label: 'Electronic service' },
  { value: 'fax',       label: 'Fax' },
  { value: 'other',     label: 'Other (note below)' },
]

// Rendered name resolver — handles ad-hoc + contact-linked rows.  Falls
// back sensibly so the row never renders blank.  Backend joins expose
// contact_first_name / contact_last_name / contact_company_name AS the
// authoritative names — panel Frontend HIGH-1 verification: use those
// aliased columns, not the un-aliased local strings.
// Ship 7i.2 CB-4 — delegates to the shared resolver so the row header, the
// PDFs, and the API all agree.  Same precedence everywhere: override →
// contact → null.
function displayWitnessName (w) {
  if (!w) return '(unknown)'
  return witnessDisplayName(w)
}

// ═══════════════════════════════════════════════════════════════════════
// Client-side validators — MIRROR backend validateCompletenessForType
// (api/src/routes/matterWitnesses.js).  Duplicating on purpose so the
// ceremony gate can fire BEFORE the confirm modal opens (panel HIGH-2:
// server 400 mid-ceremony is a UX regression).
// ═══════════════════════════════════════════════════════════════════════
function nullifTrim (v) {
  if (v == null) return null
  const s = String(v).trim()
  return s.length === 0 ? null : s
}
function validateRetainedExpertCompleteness (row) {
  const missing = []
  if (!nullifTrim(row.expert_specialty)) {
    missing.push({ field: 'expert_specialty', message: 'Expert specialty is required.', statute: 'CCP §2034.260(c)(1)' })
  }
  if (!nullifTrim(row.qualifications_summary)) {
    missing.push({ field: 'qualifications_summary', message: 'Brief narrative statement of qualifications is required.', statute: 'CCP §2034.260(c)(1)' })
  }
  if (!nullifTrim(row.subject_of_testimony)) {
    missing.push({ field: 'subject_of_testimony', message: 'General substance of the testimony expected is required (not a bare specialty label).', statute: 'CCP §2034.260(c)(2)' })
  }
  const dep = row.hourly_rate_deposition
  if (dep == null || dep === '' || !(parseFloat(dep) > 0)) {
    missing.push({ field: 'hourly_rate_deposition', message: 'Hourly deposition fee is required and must be > 0.', statute: 'CCP §2034.260(c)(5)' })
  }
  const trial = row.hourly_rate_trial
  if (trial == null || trial === '' || !(parseFloat(trial) > 0)) {
    missing.push({ field: 'hourly_rate_trial', message: 'Hourly trial fee is required and must be > 0.', statute: 'CCP §2034.260(c)(5)' })
  }
  // Empty string is a valid "none" answer; NULL/undefined is the violation.
  if (row.previously_designated_matters == null) {
    missing.push({ field: 'previously_designated_matters', message: 'Previously-designated-matters list is required (empty string is valid to represent "none").', statute: 'CCP §2034.415' })
  }
  return { ok: missing.length === 0, missing }
}
function validateNonRetainedExpertCompleteness (row) {
  const missing = []
  if (!nullifTrim(row.expert_specialty)) {
    missing.push({ field: 'expert_specialty', message: 'Expert specialty is required for a non-retained expert.', statute: 'CCP §2034.210(b)' })
  }
  if (!nullifTrim(row.subject_of_testimony)) {
    missing.push({ field: 'subject_of_testimony', message: 'General substance of the testimony expected is required.', statute: 'CCP §2034.260(c)(2)' })
  }
  return { ok: missing.length === 0, missing }
}
function validateCompletenessForType (row) {
  if (row.witness_type === 'expert_retained') return validateRetainedExpertCompleteness(row)
  if (row.witness_type === 'expert_non_retained') return validateNonRetainedExpertCompleteness(row)
  return { ok: true, missing: [] }
}
// Fold a per-field-errors 400 into a { field: message } map for inline UI.
function parsePerFieldErrors (err) {
  const list = err?.response?.data?.missing_fields
  if (!Array.isArray(list)) return {}
  const map = {}
  for (const m of list) {
    if (m && m.field && m.message) {
      map[m.field] = `${m.message}${m.statute ? ` (${m.statute})` : ''}`
    }
  }
  return map
}

// Empty form template — used for create + edit reset + reclassify seed.
function emptyForm () {
  return {
    contact_id: '',
    display_name: '',
    witness_type: 'fact',
    address: '',
    phone: '',
    email: '',
    subject_of_testimony: '',
    will_call_at_trial: false,
    subpoena_served_at: '',
    notes: '',
    expert_specialty: '',
    qualifications_summary: '',
    retention_date: '',
    hourly_rate_deposition: '',
    hourly_rate_trial: '',
    // NULL vs '' matters for §2034.260(c)(5) previously_designated_matters:
    // '' is a valid "none" declaration; null means "not answered yet".
    // Seed as null so the validator surfaces the missing gate; user must
    // explicitly answer.
    previously_designated_matters: null,
  }
}
function witnessToForm (w) {
  if (!w) return emptyForm()
  return {
    contact_id: w.contact_id != null ? String(w.contact_id) : '',
    display_name: w.display_name || '',
    witness_type: w.witness_type || 'fact',
    address: w.address || '',
    phone: w.phone || '',
    email: w.email || '',
    subject_of_testimony: w.subject_of_testimony || '',
    will_call_at_trial: !!w.will_call_at_trial,
    subpoena_served_at: w.subpoena_served_at ? String(w.subpoena_served_at).slice(0, 10) : '',
    notes: w.notes || '',
    expert_specialty: w.expert_specialty || '',
    qualifications_summary: w.qualifications_summary || '',
    retention_date: w.retention_date ? String(w.retention_date).slice(0, 10) : '',
    hourly_rate_deposition: w.hourly_rate_deposition != null ? String(w.hourly_rate_deposition) : '',
    hourly_rate_trial: w.hourly_rate_trial != null ? String(w.hourly_rate_trial) : '',
    // Preserve NULL semantic when it comes from server — '' from a save
    // will stay '' (means "none"); actual null means "not answered yet"
    // and blocks retained-expert completeness.
    previously_designated_matters: w.previously_designated_matters == null ? '' : String(w.previously_designated_matters),
  }
}
// Ship 7i.2 CB-4 — `contactMode` decides the IDENTITY half of the payload.
//
//   'existing' / 'create' → send contact_id, OMIT display_name entirely
//   'ad_hoc'              → send contact_id: null + display_name
//
// The DB enforces XOR (ck_matter_witnesses_identity): exactly one of
// {contact_id, display_name}.  Sending both — which this function used to do
// unconditionally — is now a 400.  display_name is OMITTED (not sent as null)
// for contact-linked rows because the PUT handler treats an explicit null as
// "the caller is trying to edit the name here", which is only legal on ad-hoc
// rows.  `isEdit` additionally suppresses identity fields on PUT, where
// identity is immutable for contact-linked rows.
function formToPayload (form, contactMode = 'existing', isEdit = false) {
  const numOrNull = (v) => {
    if (v === '' || v == null) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  const s = (v) => {
    if (v == null) return null
    const t = String(v).trim()
    return t === '' ? null : t
  }
  const t = form.witness_type
  const isExpert   = isExpertType(t)
  const isRetained = isRetainedExpertType(t)
  const isAdHoc = contactMode === 'ad_hoc'

  // Identity half — mutually exclusive, and omitted entirely on edit of a
  // contact-linked row (identity there lives on the contact record).
  const identity = isAdHoc
    ? { contact_id: null, display_name: s(form.display_name) }
    : (isEdit ? {} : { contact_id: form.contact_id ? Number(form.contact_id) : null })

  return {
    ...identity,
    witness_type: t,
    // Ship 7i.2 CB-4 — per-witness SERVICE-DETAIL overrides.  These used to be
    // collected by this form and silently dropped by the server.  A nonblank
    // value here wins over the contact card; '' clears back to the contact.
    address: s(form.address),
    phone: s(form.phone),
    email: s(form.email),
    subject_of_testimony: s(form.subject_of_testimony),
    will_call_at_trial: !!form.will_call_at_trial,
    subpoena_served_at: s(form.subpoena_served_at),
    notes: s(form.notes),
    // Expert-only fields.  Non-expert types send null so a witness_type
    // transition (edit path — same-type-only in Ship 7i.1) never persists
    // stale data.
    expert_specialty:               isExpert   ? s(form.expert_specialty)               : null,
    qualifications_summary:         isExpert   ? s(form.qualifications_summary)         : null,
    retention_date:                 isRetained ? s(form.retention_date)                 : null,
    hourly_rate_deposition:         isExpert   ? numOrNull(form.hourly_rate_deposition) : null,
    hourly_rate_trial:              isRetained ? numOrNull(form.hourly_rate_trial)      : null,
    // Preserve NULL vs '' semantic — MUST send null (not '') when the
    // caller hasn't answered so backend validator rejects incomplete
    // retained-expert writes.  Empty string is a valid "none".
    previously_designated_matters:  isRetained
      ? (form.previously_designated_matters == null ? null : String(form.previously_designated_matters))
      : null,
  }
}

// FILTER chips
const FILTER_CHIPS = [
  { key: 'all',                 label: 'All' },
  { key: 'fact',                label: 'Fact witnesses' },
  { key: 'expert_retained',     label: 'Expert (retained)' },
  { key: 'expert_non_retained', label: 'Expert (non-retained)' },
]

// ═══════════════════════════════════════════════════════════════════════
// PARENT — MatterWitnessesCard
// ═══════════════════════════════════════════════════════════════════════
export default function MatterWitnessesCard ({
  matterId,
  matterContacts = [],
  canEdit = false,
  onContactCreated,
}) {
  const ask = useAsk()
  const fid = useFieldIds()

  const [witnesses, setWitnesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [filter, setFilter] = useState('all')
  const [showSuperseded, setShowSuperseded] = useState(false)
  // Independent of showSuperseded: a designation can be replaced, pulled,
  // both, or neither.  Default off so the roster shows who the firm is
  // actually preparing to call.
  const [showWithdrawn, setShowWithdrawn] = useState(false)

  // Modal state — polymorphic.  Only one modal open at a time.
  //   { mode: 'create' }
  //   { mode: 'edit',       witness }
  //   { mode: 'reclassify', witness }   — dedicated supersede/coexist flow
  //   { mode: 'disclose',   witness }   — record disclosure event
  //   { mode: 'events',     witness }   — read-only events list panel
  const [modal, setModal] = useState(null)

  // Expanded per-row disclosure-events accordion state.  Keys = witness.id.
  const [expandedEvents, setExpandedEvents] = useState({})

  // ── Ship 7i coexist model — "Also in Cast" cross-link marker ───────────
  // The Witnesses roster and the Case Analysis Cast are deliberately NOT
  // auto-synced (Ship 7i panel H1): a Cast member is attorney work-product
  // derived from fact links, a witness is a CCP §2034 disclosure-roster
  // entry.  This map is the READ-ONLY half of that contract — it lets a
  // witness row say "this person is also tied to N chronology facts"
  // without either surface writing to the other.
  //
  //   contact_id (number) → fact_count (number)
  //
  // Both key sides are int4 (contacts.id / matter_witnesses.contact_id),
  // which node-pg parses to a JS Number, so Map lookups key-match.  Only
  // COUNT() comes back as a bigint STRING — hence the Number() below.  It
  // is load-bearing, not defensive: without it the singular/plural branch
  // compares '1' === 1 and is silently always false.
  const [castByContactId, setCastByContactId] = useState(() => new Map())

  // Best-effort, and deliberately so.  /matter/:id/cast is gated
  // assertMatterAccess(..., { permission: 'matters' }), which is NOT the
  // same gate as this card, so a caller who can legitimately see the
  // witness roster may still be refused the cast.  A denial MUST degrade
  // to "no chips" and must never throw into the render: this card feeds
  // the CCP §2034.260 Expert Designation PDF, and blanking a court-filing
  // surface because a decorative marker failed to load is unacceptable.
  // Do not remove the .catch on the grounds that the call "should" succeed.
  //
  // The try/catch is NOT redundant with the .catch.  A .catch only handles a
  // REJECTED promise; a SYNCHRONOUS throw out of the call itself — a malformed
  // axios config, a request interceptor that throws, or the helper simply not
  // being a function — escapes it and propagates out of the effect body to the
  // nearest error boundary, which is exactly the blanked court-filing surface
  // this guard exists to prevent.  Verified: removing the try/catch and
  // leaving getCaseCast unstubbed throws "getCaseCast is not a function" from
  // the effect and takes the whole card down with it.
  useEffect(() => {
    if (!matterId) { setCastByContactId(new Map()); return undefined }
    let cancelled = false
    const clear = () => { if (!cancelled) setCastByContactId(new Map()) }
    try {
      Promise.resolve(getCaseCast(matterId))
        .then(r => {
          if (cancelled) return
          const rows = Array.isArray(r?.data) ? r.data : []
          setCastByContactId(new Map(
            rows.map(row => [row.id, Number(row.fact_count) || 0])
          ))
        })
        .catch(clear)
    } catch (_) {
      clear()
    }
    return () => { cancelled = true }
  }, [matterId])

  const load = async () => {
    if (!matterId) return
    setLoading(true); setErr('')
    try {
      const r = await listMatterWitnesses(matterId, { includeSuperseded: showSuperseded, includeWithdrawn: showWithdrawn })
      const rows = Array.isArray(r?.data)
        ? r.data
        : (Array.isArray(r?.data?.items) ? r.data.items : [])
      setWitnesses(rows)
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not load witnesses')
    } finally {
      setLoading(false)
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [matterId, showSuperseded, showWithdrawn])

  // Panel Frontend HIGH-1 handler.  When the supersession endpoint returns
  // 409 with an error code that means "stale record", we refetch + show a
  // user-friendly banner instead of a silent overwrite.
  const showStaleBanner = (msg) => {
    setErr(
      `⚠ ${msg}  The witness list has been refreshed to show the current designation.` +
      '  Please review and try your change again.'
    )
    load()
  }

  const removeWitness = async (w) => {
    if (!canEdit) return
    const ok = await ask({
      type: 'confirm',
      title: 'Remove witness?',
      message:
        `Remove "${displayWitnessName(w)}" from the witness roster for this matter.\n\n` +
        'This is a hard delete — the row is only removable if it has NEVER been ' +
        'served in a §2034 disclosure event.  Once you serve the designation the ' +
        'row becomes part of the §2034.300 evidentiary record and must be ' +
        'superseded instead of deleted.',
      confirmLabel: 'Remove witness',
      cancelLabel: 'Cancel',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteMatterWitness(w.id)
      load()
    } catch (e) {
      // Backend friendly 409s translate to specific error banners.
      const code = e?.response?.data?.error
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Could not remove witness'
      const hint = e?.response?.data?.hint
      if (code === 'row_has_disclosure_events' || code === 'row_has_successor' || code === 'row_participates_in_chain') {
        setErr(`${msg}${hint ? ` — ${hint}` : ''}`)
      } else {
        setErr(msg)
      }
    }
  }

  // Soft-delete.  `ask` is declared above at the top of this component, well
  // ahead of this reader.
  //
  // The modal states that withdrawal is TERMINAL in as many words, because it
  // is: the server writes withdrawn_at once and never clears it, and there is
  // no un-withdraw endpoint.  Putting the witness back is a re-designation
  // (Change witness type), which leaves the withdrawal on the record.  An
  // attorney who clicks this expecting an undo has to be told before the call,
  // not after.
  const withdrawWitness = async (w) => {
    const reason = await ask({
      title: 'Withdraw this designation',
      message:
        `Withdraw ${displayWitnessName(w)} from this matter's designations?\n\n` +
        'They come off the roster, the Master Witness List, and any Expert ' +
        'Designation you generate from here on. Nothing already served is ' +
        'altered, and the disclosure history stays intact.\n\n' +
        'This cannot be undone. To designate this person again later you will ' +
        'record a new designation that supersedes this one — the withdrawal ' +
        'stays on the record either way.\n\n' +
        'Why is the designation being withdrawn? A §2034.300 motion can ask ' +
        'about this years from now.',
      type: 'textarea',
      required: true,
      maxLength: 2000,
      placeholder: 'e.g. Expert is unavailable for the trial date and will not be called.',
      confirmLabel: 'Withdraw designation',
      cancelLabel: 'Cancel',
      danger: true,
    })
    if (reason == null) return
    const trimmed = String(reason).trim()
    // Mirrors the server's WITHDRAWAL_REASON_MIN so the attorney learns the
    // rule without a round-trip.  The server remains authoritative.
    if (trimmed.length < 10) {
      setErr('Withdrawal reason must be at least 10 characters — it is the record of why this designation was pulled.')
      return
    }
    try {
      setErr('')
      await withdrawMatterWitness(w.id, trimmed)
      load()
    } catch (e) {
      const code = e?.response?.data?.error
      const msg = e?.response?.data?.message || 'Could not withdraw this designation'
      const hint = e?.response?.data?.hint
      if (code === 'already_withdrawn' || code === 'row_superseded') {
        // Someone else changed this row while the modal was open — refetch so
        // the roster shows the truth rather than leaving a stale button.
        setErr(`${msg}${hint ? ` — ${hint}` : ''}`)
        load()
      } else {
        setErr(`${msg}${hint ? ` — ${hint}` : ''}`)
      }
    }
  }

  const filteredWitnesses = useMemo(() => {
    if (filter === 'all') return witnesses
    if (filter === 'fact') {
      return witnesses.filter(w =>
        w.witness_type === 'fact' || w.witness_type === 'percipient'
      )
    }
    return witnesses.filter(w => w.witness_type === filter)
  }, [witnesses, filter])

  const chipCounts = useMemo(() => {
    const c = { all: 0, fact: 0, expert_retained: 0, expert_non_retained: 0 }
    for (const w of witnesses) {
      // Chips reflect ACTIVE rows only — a superseded fact-witness that
      // was replaced by a retained-expert would inflate both buckets if
      // we counted every row.  Roster totals below the table can show
      // included-superseded when the toggle is on.
      if (w.superseded_at != null) continue
      // Withdrawn rows are off the active designation for the same reason and
      // must not inflate a chip the attorney reads as "who we are calling".
      if (w.withdrawn_at != null) continue
      c.all++
      if (w.witness_type === 'fact' || w.witness_type === 'percipient') c.fact++
      else if (w.witness_type === 'expert_retained') c.expert_retained++
      else if (w.witness_type === 'expert_non_retained') c.expert_non_retained++
    }
    return c
  }, [witnesses])

  const supersededCount = useMemo(
    () => witnesses.filter(w => w.superseded_at != null).length,
    [witnesses]
  )

  const withdrawnCount = useMemo(
    () => witnesses.filter(w => w.withdrawn_at != null).length,
    [witnesses]
  )

  const toggleEvents = (witnessId) =>
    setExpandedEvents(prev => ({ ...prev, [witnessId]: !prev[witnessId] }))

  return (
    <div id="witnesses-inner">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-b border-gray-100">
        <div className="flex flex-wrap gap-2">
          {FILTER_CHIPS.map(c => {
            const active = filter === c.key
            const cnt = chipCounts[c.key] ?? 0
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-purple-50'
                }`}
              >
                {c.label}
                {cnt > 0 && (
                  <span className={`ml-1.5 text-[10px] ${active ? 'text-purple-100' : 'text-gray-500'}`}>
                    ({cnt})
                  </span>
                )}
              </button>
            )
          })}
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 ml-2 pl-3 border-l border-gray-200">
            <input
              type="checkbox"
              checked={showSuperseded}
              onChange={e => setShowSuperseded(e.target.checked)}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            Show superseded
            {supersededCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium">
                {supersededCount}
              </span>
            )}
          </label>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 ml-2 pl-3 border-l border-gray-200">
            <input
              type="checkbox"
              checked={showWithdrawn}
              onChange={e => setShowWithdrawn(e.target.checked)}
              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            Show withdrawn
            {withdrawnCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-medium">
                {withdrawnCount}
              </span>
            )}
          </label>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => setModal({ mode: 'create' })}
            className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
          >
            + Add Witness
          </button>
        )}
      </div>

      {err && (
        <div className="mx-6 my-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2">
          <div className="flex-1 whitespace-pre-wrap">{err}</div>
          <button type="button" onClick={() => setErr('')} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <p className="px-6 py-6 text-xs text-gray-400 italic">Loading witnesses…</p>
      ) : filteredWitnesses.length === 0 ? (
        witnesses.length === 0 ? (
          <EmptyState
            className="py-8"
            icon="🎤"
            title="No witnesses on this matter yet"
            description={
              'Track percipient witnesses AND retained/non-retained experts here. ' +
              'This roster is the CCP §2034 disclosure list — separate from the ' +
              'Case Analysis Cast tab (which is fact-derived work-product).'
            }
            action={canEdit ? {
              label: '+ Add first witness',
              onClick: () => setModal({ mode: 'create' })
            } : undefined}
          />
        ) : (
          <EmptyState
            className="py-6"
            icon="🔍"
            title="No witnesses match this filter"
            description="Try a different filter or clear it."
            variant="filtered"
            action={{ label: 'Show all', onClick: () => setFilter('all') }}
          />
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-left">
              <tr>
                <th className="px-6 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Type</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">Subject of Testimony</th>
                <th className="px-6 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">Will Call</th>
                {canEdit && <th className="px-6 py-2 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredWitnesses.map(w => {
                const meta = witnessTypeMeta(w.witness_type)
                const superseded = w.superseded_at != null
                const withdrawn = w.withdrawn_at != null
                // Both states grey the row out; the CHIPS are what distinguish
                // "replaced by a successor" from "pulled by this firm".
                const offRoster = superseded || withdrawn
                // Ad-hoc witnesses carry display_name with contact_id NULL
                // (schema enforces the XOR), so they can NEVER have a Cast
                // match.  They must render no chip at all — never "0 facts".
                const castFacts = w.contact_id != null
                  ? (castByContactId.get(w.contact_id) || 0)
                  : 0
                return (
                  <Fragment key={w.id}>
                    <tr className={withdrawn ? 'bg-rose-50/40 hover:bg-rose-50' : (superseded ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-gray-50')}>
                      <td className="px-6 py-3 align-top">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${meta.chip} ${offRoster ? 'opacity-60' : ''}`}>
                          {meta.label}
                        </span>
                        {superseded && (
                          <div className="mt-1">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                              SUPERSEDED {new Date(w.superseded_at).toLocaleDateString('en-CA')}
                            </span>
                          </div>
                        )}
                        {withdrawn && (
                          <div className="mt-1">
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-900 border border-rose-300"
                              title={w.withdrawal_reason || undefined}
                            >
                              WITHDRAWN {new Date(w.withdrawn_at).toLocaleDateString('en-CA')}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-3 align-top">
                        <div className={`font-medium ${offRoster ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                          {displayWitnessName(w)}
                        </div>
                        {w.contact_company_name && (
                          <div className={`text-xs ${offRoster ? 'text-gray-400' : 'text-gray-500'}`}>🏢 {w.contact_company_name}</div>
                        )}
                        {isExpertType(w.witness_type) && w.expert_specialty && (
                          <div className={`text-xs ${offRoster ? 'text-gray-400' : 'text-purple-700'}`}>🎓 {w.expert_specialty}</div>
                        )}
                        {w.prior_row_id && (
                          <div className="text-[11px] text-purple-600 mt-0.5">
                            ↑ replaces prior row #{w.prior_row_id}
                          </div>
                        )}
                        {/* Ship 7i coexist cross-link.  Fuchsia matches the
                            Case Analysis accordion accent, so the colour
                            itself says which subsystem this points at.
                            Deliberately NOT a link: Case Analysis lives in a
                            different accordion and deep-linking to one Cast
                            member is a separate navigation concern — a chip
                            that looks clickable and is not is worse than a
                            plain one.  The ABSENCE of this marker does not
                            mean the person is unimportant; it means they are
                            not tied to a chronology fact. */}
                        {castFacts > 0 && (
                          <div className={`text-[11px] mt-0.5 ${superseded ? 'text-gray-400' : 'text-fuchsia-700'}`}>
                            🧩 Also in Cast ({castFacts} fact{castFacts === 1 ? '' : 's'})
                          </div>
                        )}
                      </td>
                      {/* Ship 7i.2 CB-4 — RESOLVED service details, not the raw
                          contact card.  This column is what gets served on the
                          §2034.260(c)(1) designation, so it must show the
                          per-witness override when one is set.  A small chip
                          marks overridden fields so the attorney can see at a
                          glance that this row does NOT match the rolodex. */}
                      <td className={`px-6 py-3 align-top text-xs ${superseded ? 'text-gray-400' : 'text-gray-600'}`}>
                        {(() => {
                          const r = resolveWitnessIdentity(w)
                          const chip = (src) => src === 'override'
                            ? <span className="ml-1 px-1 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-medium align-middle">override</span>
                            : null
                          if (!r.address && !r.phone && !r.email) {
                            return <span className="text-gray-400 italic">—</span>
                          }
                          return (
                            <>
                              {r.address && <div>📍 {r.address}{chip(r.address_source)}</div>}
                              {r.phone   && <div>📞 {r.phone}{chip(r.phone_source)}</div>}
                              {r.email   && <div>📧 {r.email}{chip(r.email_source)}</div>}
                            </>
                          )
                        })()}
                      </td>
                      <td className={`px-6 py-3 align-top text-xs max-w-md ${superseded ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                        {w.subject_of_testimony
                          ? <span className="whitespace-pre-wrap">{w.subject_of_testimony}</span>
                          : <span className="text-gray-400 italic">Not stated yet</span>}
                      </td>
                      <td className="px-6 py-3 align-top text-center">
                        {w.will_call_at_trial
                          ? <span className={offRoster ? 'text-gray-400' : 'text-emerald-700 font-medium'}>✓ Yes</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      {canEdit && (
                        <td className="px-6 py-3 align-top text-right whitespace-nowrap">
                          <div className="flex flex-col items-end gap-1">
                            {!offRoster && (
                              <>
                                <button type="button"
                                  onClick={() => setModal({ mode: 'edit', witness: w })}
                                  className="text-xs text-primary-700 hover:underline">
                                  Edit
                                </button>
                                <button type="button"
                                  onClick={() => setModal({ mode: 'reclassify', witness: w })}
                                  className="text-xs text-amber-700 hover:underline"
                                  title="Change classification — records a §2034.270 supersession event.">
                                  Change witness type
                                </button>
                                <button type="button"
                                  onClick={() => setModal({ mode: 'disclose', witness: w })}
                                  className="text-xs text-emerald-700 hover:underline">
                                  Record Disclosure
                                </button>
                              </>
                            )}
                            <button type="button"
                              onClick={() => toggleEvents(w.id)}
                              className="text-xs text-gray-600 hover:underline">
                              {expandedEvents[w.id] ? '▾ Hide events' : '▸ Disclosure events'}
                            </button>
                            {withdrawn && !superseded && (
                              <button type="button"
                                onClick={() => setModal({ mode: 'reclassify', witness: w })}
                                className="text-xs text-purple-700 hover:underline"
                                title="Designate this person again. Records a new designation that supersedes the withdrawn one; the withdrawal stays on the record.">
                                Re-designate
                              </button>
                            )}
                            {!offRoster && (
                              <button type="button"
                                onClick={() => withdrawWitness(w)}
                                className="text-xs text-rose-700 hover:underline"
                                title="Take this designation off the roster and off any Expert Designation, without touching what has already been served. Permanent.">
                                Withdraw
                              </button>
                            )}
                            {!offRoster && (
                              <button type="button"
                                onClick={() => removeWitness(w)}
                                className="text-xs text-red-600 hover:underline">
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {expandedEvents[w.id] && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={canEdit ? 6 : 5} className="px-6 pt-2 pb-4">
                          <DisclosureEventsPanel witnessId={w.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {modal?.mode === 'create' && (
        <WitnessFormModal
          matterId={matterId}
          matterContacts={matterContacts}
          mode="create"
          witness={null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
          onContactCreated={onContactCreated}
          onStaleRecord={showStaleBanner}
          fid={fid}
        />
      )}
      {modal?.mode === 'edit' && (
        <WitnessFormModal
          matterId={matterId}
          matterContacts={matterContacts}
          mode="edit"
          witness={modal.witness}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load() }}
          onContactCreated={onContactCreated}
          onStaleRecord={showStaleBanner}
          fid={fid}
        />
      )}
      {modal?.mode === 'reclassify' && (
        <ReclassifyWitnessModal
          matterId={matterId}
          witness={modal.witness}
          onClose={() => setModal(null)}
          onReclassified={() => { setModal(null); load() }}
          onStaleRecord={showStaleBanner}
          ask={ask}
          fid={fid}
        />
      )}
      {modal?.mode === 'disclose' && (
        <RecordDisclosureModal
          witness={modal.witness}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            setExpandedEvents(prev => ({ ...prev, [modal.witness.id]: true }))
            // Silent — the panel below will fetch fresh on expand.
          }}
          fid={fid}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// WitnessFormModal — add + same-type-only edit.  witness_type is a
// dropdown ONLY in create mode; in edit mode it's a DISPLAY chip with a
// link to Change witness type (the reclassify flow).  This is the
// backend contract in Ship 7i.1: PUT /:id refuses witness_type change
// with 409 pointing at POST supersession.
// ═══════════════════════════════════════════════════════════════════════
function WitnessFormModal ({
  matterId, matterContacts, mode, witness,
  onClose, onSaved, onContactCreated, onStaleRecord, fid,
}) {
  const [form, setForm] = useState(() => witnessToForm(witness))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [contactMode, setContactMode] = useState(() => {
    if (mode === 'edit') return witness?.contact_id ? 'existing' : 'ad_hoc'
    return 'existing'
  })
  const [contactFilter, setContactFilter] = useState('')
  const [allContacts, setAllContacts] = useState(null)

  const isExpert   = isExpertType(form.witness_type)
  const isRetained = isRetainedExpertType(form.witness_type)

  const submit = async () => {
    setErr(''); setFieldErrors({})
    if (contactMode === 'ad_hoc' && !form.display_name.trim()) {
      setErr('Enter a display name for the witness (no Contact selected).')
      return
    }
    if (contactMode === 'existing' && !form.contact_id) {
      setErr('Pick a Contact from the list, or switch to Ad-hoc / Create new contact.')
      return
    }

    // Client-side 5-field / 2-field completeness — panel HIGH-2: catch
    // BEFORE hitting the API so a 400 response isn't the ceremony teacher.
    const check = validateCompletenessForType({
      witness_type: form.witness_type,
      expert_specialty: form.expert_specialty,
      qualifications_summary: form.qualifications_summary,
      subject_of_testimony: form.subject_of_testimony,
      hourly_rate_deposition: form.hourly_rate_deposition,
      hourly_rate_trial: form.hourly_rate_trial,
      previously_designated_matters: form.previously_designated_matters,
    })
    if (!check.ok) {
      const map = {}
      for (const m of check.missing) map[m.field] = `${m.message} (${m.statute})`
      setFieldErrors(map)
      setErr(`Missing required ${isRetained ? 'retained-expert' : 'expert'} fields — see red highlights below.`)
      return
    }

    setBusy(true)
    try {
      const payload = formToPayload(form, contactMode, mode === 'edit')
      if (mode === 'create') {
        // Panel path — plain create.  If the (matter, contact) already
        // has an active row the backend refuses 409 with a hint pointing
        // at the supersession endpoint.  We surface a specific banner
        // and refresh instead of retrying under the hood.
        await createMatterWitness({
          matter_id: matterId,
          ...payload,
        })
      } else {
        // Edit — same witness_type only.  Backend refuses type change
        // with 409 error='type_change_requires_supersession'.  We keep
        // the select disabled in edit mode so this path should never
        // fire, but if it does we surface the message clearly.
        await updateMatterWitness(witness.id, payload)
      }
      onSaved()
    } catch (e) {
      const status = e?.response?.status
      const code = e?.response?.data?.error
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Could not save witness'
      // Per-field 400 from the completeness validator (server-side
      // belt-and-suspenders vs. our client-side check).
      if (status === 400 && e?.response?.data?.missing_fields) {
        setFieldErrors(parsePerFieldErrors(e))
        setErr(`Missing required fields — see red highlights below.`)
      } else if (status === 409 && code === 'active_witness_exists') {
        setErr(
          `${msg}  Use Change witness type on the existing row (or Remove it) — creating a second active witness row is not allowed.`
        )
      } else if (status === 409 && code === 'row_superseded') {
        onStaleRecord?.(msg)
        onClose()
      } else if (status === 409 && code === 'type_change_requires_supersession') {
        setErr(msg + '  Use the Change witness type button on the row instead.')
      } else {
        setErr(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  const { containerProps } = useModal({ onClose, onSubmit: submit })

  const seedFromMatter = Array.isArray(matterContacts) ? matterContacts : []
  const globalPool = Array.isArray(allContacts) ? allContacts : []
  const contactPool = allContacts != null ? globalPool : seedFromMatter
  const filteredContacts = useMemo(() => {
    const q = contactFilter.trim().toLowerCase()
    const rows = q
      ? contactPool.filter(c => {
          const bag = [
            c.first_name, c.last_name, c.company_name, c.email, c.phone
          ].filter(Boolean).join(' ').toLowerCase()
          return bag.includes(q)
        })
      : contactPool
    return rows.slice(0, 20)
  }, [contactPool, contactFilter])

  const loadAllContacts = async () => {
    if (allContacts != null) return
    try {
      const r = await api.get('/api/contacts')
      setAllContacts(Array.isArray(r?.data) ? r.data : (r?.data?.items || []))
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not load contact list')
      setAllContacts([])
    }
  }

  const pickedContact = form.contact_id
    ? [...seedFromMatter, ...globalPool].find(c => String(c.id) === String(form.contact_id))
    : null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div {...containerProps} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold text-gray-800">
            {mode === 'create' ? '+ Add Witness' : 'Edit Witness'}
          </h3>
          <button type="button" onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-5">
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap">
              {err}
            </div>
          )}

          {/* Contact source — CREATE mode only. */}
          {mode === 'create' && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contact source</label>
              <div className="flex flex-wrap gap-2 text-xs">
                {[
                  { key: 'existing', label: 'Link existing contact' },
                  { key: 'ad_hoc',   label: 'Ad-hoc (no contact record)' },
                  { key: 'create',   label: '+ Create new contact' },
                ].map(opt => (
                  <button key={opt.key} type="button"
                    onClick={() => setContactMode(opt.key)}
                    className={`px-3 py-1 rounded-full border ${
                      contactMode === opt.key
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-purple-50'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {contactMode === 'existing' && (
            <div>
              <label htmlFor={fid('witness-contact-picker')} className="block text-xs font-medium text-gray-600 mb-1">
                Contact
              </label>
              <input
                id={fid('witness-contact-picker')} type="text"
                value={contactFilter}
                onChange={e => { setContactFilter(e.target.value); if (form.contact_id) setForm({ ...form, contact_id: '' }) }}
                placeholder="Search this matter's contacts by name, company, email, phone"
                data-autofocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {!pickedContact && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded mt-1 bg-white">
                  {filteredContacts.length === 0 ? (
                    <div className="p-2 text-xs text-gray-400 italic">
                      No matches.{' '}
                      {allContacts == null && (
                        <button type="button" onClick={loadAllContacts}
                          className="text-primary-700 hover:underline">Search all firm contacts →</button>
                      )}
                    </div>
                  ) : filteredContacts.map(c => (
                    <button key={c.id} type="button"
                      onClick={() => setForm({ ...form, contact_id: String(c.id) })}
                      className="w-full text-left p-2 hover:bg-purple-50 border-b border-gray-100 last:border-b-0 text-xs">
                      <span className="text-gray-800 font-medium">
                        {c.first_name || ''} {c.last_name || ''}
                      </span>
                      {c.company_name && <span className="text-gray-500 ml-2">· {c.company_name}</span>}
                      {c.email && <span className="text-gray-400 ml-2">{c.email}</span>}
                    </button>
                  ))}
                  {allContacts == null && filteredContacts.length > 0 && (
                    <div className="p-2 text-[11px] bg-gray-50 border-t border-gray-100 text-gray-500">
                      Showing matter's linked contacts.{' '}
                      <button type="button" onClick={loadAllContacts}
                        className="text-primary-700 hover:underline">Search all firm contacts →</button>
                    </div>
                  )}
                </div>
              )}
              {pickedContact && (
                <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded flex items-center gap-2 text-xs">
                  <span className="flex-1">
                    ✓ {pickedContact.first_name || ''} {pickedContact.last_name || ''}
                    {pickedContact.company_name && <span className="text-gray-500 ml-1">· {pickedContact.company_name}</span>}
                  </span>
                  <button type="button"
                    onClick={() => setForm({ ...form, contact_id: '' })}
                    className="text-purple-700 hover:underline">change</button>
                </div>
              )}
            </div>
          )}

          {contactMode === 'ad_hoc' && (
            <div>
              <label htmlFor={fid('witness-display-name')} className="block text-xs font-medium text-gray-600 mb-1">
                Display name <span className="text-red-500">*</span>
              </label>
              <input
                id={fid('witness-display-name')} type="text"
                value={form.display_name}
                onChange={e => setForm({ ...form, display_name: e.target.value })}
                placeholder="e.g. John Smith, Officer Rodriguez"
                data-autofocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Ad-hoc witnesses are not stored in the general Contacts list.
              </p>
            </div>
          )}

          {contactMode === 'create' && (
            <InlineWitnessContactCreator
              matterId={matterId}
              suggestedGroup={isExpert ? 'expert_witness' : 'witness'}
              onCreated={async (newContact) => {
                if (typeof onContactCreated === 'function') {
                  try { await onContactCreated(newContact) } catch {}
                }
                setForm(f => ({ ...f, contact_id: String(newContact.id) }))
                setContactMode('existing')
                setContactFilter(
                  `${newContact.first_name || ''} ${newContact.last_name || ''}`.trim()
                )
              }}
            />
          )}

          {/* Witness type — ONLY editable in create mode.  In edit mode
              show a read-only chip + link to the reclassify flow. */}
          <div>
            <label htmlFor={fid('witness-type')} className="block text-xs font-medium text-gray-600 mb-1">
              Witness type <span className="text-red-500">*</span>
            </label>
            {mode === 'create' ? (
              <>
                <select
                  id={fid('witness-type')}
                  value={form.witness_type}
                  onChange={e => setForm({ ...form, witness_type: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {WITNESS_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  {witnessTypeMeta(form.witness_type).desc}
                </p>
              </>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${witnessTypeMeta(form.witness_type).chip}`}>
                    {witnessTypeMeta(form.witness_type).label}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Locked in edit mode — reclassifying is a §2034.270 supersession event.
                  </span>
                </div>
                <p className="text-[11px] text-gray-500">
                  Close this modal and click <strong>Change witness type</strong> on the row to record a supersession.
                </p>
              </div>
            )}
          </div>

          {/* Ship 7i.2 CB-4 — per-witness SERVICE-DETAIL overrides.  These
              fields used to be collected here and silently dropped by the
              server; they now persist and WIN over the contact card. */}
          <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3 space-y-3">
            <div>
              <div className="text-xs font-semibold text-purple-900">Service details</div>
              <p className="text-[11px] text-purple-800 mt-0.5">
                {contactMode === 'ad_hoc'
                  ? 'This ad-hoc witness has no contact card, so these are the only service details on file. The address is what prints on a CCP §2034.260(c)(1) designation.'
                  : 'Leave blank to use the linked contact\'s details. Anything you type here OVERRIDES the contact card for this matter only — use it when an expert should be served at counsel\'s office rather than the address in the rolodex.'}
              </p>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor={fid('witness-phone')} className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input id={fid('witness-phone')} type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label htmlFor={fid('witness-email')} className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input id={fid('witness-email')} type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
          </div>
          <div>
            <label htmlFor={fid('witness-address')} className="block text-xs font-medium text-gray-600 mb-1">
              Service address
              {isExpert && <span className="text-[10px] text-purple-700 ml-1">· CCP §2034.260(c)(1)</span>}
            </label>
            <input id={fid('witness-address')} type="text"
              value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              placeholder={contactMode === 'ad_hoc' ? 'Street, City, State ZIP' : 'Leave blank to use the contact\'s address'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          </div>

          {/* Subject of testimony — always required per §2034.260(c)(2) for
              experts + strongly encouraged for percipient/fact.  Red star
              when expert. */}
          <div>
            <label htmlFor={fid('witness-subject')} className="block text-xs font-medium text-gray-600 mb-1">
              Subject of testimony {isExpert && <span className="text-red-500">*</span>}
            </label>
            <textarea id={fid('witness-subject')} rows={3}
              value={form.subject_of_testimony}
              onChange={e => setForm({ ...form, subject_of_testimony: e.target.value })}
              placeholder={isExpert
                ? 'General substance of the testimony expected (CCP §2034.260(c)(2)) — e.g. "Standard of care in orthopedic surgery for lumbar fusion; whether the treating surgeon breached that standard; causation of plaintiff\'s claimed injuries."'
                : 'What this witness is expected to testify about.'}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                fieldErrors.subject_of_testimony ? 'border-red-500' : 'border-gray-300'
              }`} />
            {fieldErrors.subject_of_testimony && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.subject_of_testimony}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 pt-6">
              <input type="checkbox"
                checked={!!form.will_call_at_trial}
                onChange={e => setForm({ ...form, will_call_at_trial: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
              Will call at trial
            </label>
            <div>
              <label htmlFor={fid('witness-subpoena')} className="block text-xs font-medium text-gray-600 mb-1">
                Subpoena served
              </label>
              <input id={fid('witness-subpoena')} type="date"
                value={form.subpoena_served_at}
                onChange={e => setForm({ ...form, subpoena_served_at: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
          </div>

          {isExpert && (
            <ExpertFieldsSection
              form={form} setForm={setForm}
              fieldErrors={fieldErrors}
              isRetained={isRetained}
              fid={fid}
            />
          )}

          <div>
            <label htmlFor={fid('witness-notes')} className="block text-xs font-medium text-gray-600 mb-1">
              Internal notes
            </label>
            <textarea id={fid('witness-notes')} rows={2}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Attorney notes — not disclosed to opposing counsel"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy}
            className="px-4 py-2 text-sm rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50">
            {busy ? 'Saving…' : (mode === 'create' ? 'Add Witness' : 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// ExpertFieldsSection — shared expert-witness fields with per-field
// red highlights driven by fieldErrors map.  Reused by WitnessFormModal
// and ReclassifyWitnessModal so the 5-field UX is identical.
// ═══════════════════════════════════════════════════════════════════════
function ExpertFieldsSection ({ form, setForm, fieldErrors, isRetained, fid }) {
  const errCls = (name) => fieldErrors[name] ? 'border-red-500' : 'border-gray-300'
  return (
    <div className="rounded-lg bg-purple-50 border border-purple-200 p-4 space-y-3">
      <div className="text-xs font-semibold text-purple-800">
        🎓 Expert-witness fields
        {isRetained
          ? ' — CCP §2034.260(c) 5-field disclosure requirements'
          : ' — non-retained (§2034.210(b))'}
      </div>

      <div>
        <label htmlFor={fid('witness-expert-specialty')} className="block text-xs font-medium text-gray-600 mb-1">
          Specialty / area of expertise <span className="text-red-500">*</span>
        </label>
        <input id={fid('witness-expert-specialty')} type="text"
          value={form.expert_specialty}
          onChange={e => setForm({ ...form, expert_specialty: e.target.value })}
          placeholder="e.g. Orthopedic surgery, Structural engineering, Forensic accounting"
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errCls('expert_specialty')}`} />
        {fieldErrors.expert_specialty && (
          <p className="text-xs text-red-600 mt-1">{fieldErrors.expert_specialty}</p>
        )}
      </div>

      {isRetained && (
        <div>
          <label htmlFor={fid('witness-quals')} className="block text-xs font-medium text-gray-600 mb-1">
            Qualifications (narrative) <span className="text-red-500">*</span>
          </label>
          <textarea id={fid('witness-quals')} rows={3}
            value={form.qualifications_summary}
            onChange={e => setForm({ ...form, qualifications_summary: e.target.value })}
            placeholder="Board certifications, education, licensure, publications, teaching, prior testimony — the disclosure that goes to opposing counsel."
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errCls('qualifications_summary')}`} />
          {fieldErrors.qualifications_summary && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.qualifications_summary}</p>
          )}
        </div>
      )}

      {isRetained && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label htmlFor={fid('witness-rate-depo')} className="block text-xs font-medium text-gray-600 mb-1">
              Hourly fee — deposition ($) <span className="text-red-500">*</span>
            </label>
            <input id={fid('witness-rate-depo')} type="number" step="0.01" min="0"
              value={form.hourly_rate_deposition}
              onChange={e => setForm({ ...form, hourly_rate_deposition: e.target.value })}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errCls('hourly_rate_deposition')}`} />
            {fieldErrors.hourly_rate_deposition && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.hourly_rate_deposition}</p>
            )}
          </div>
          <div>
            <label htmlFor={fid('witness-rate-trial')} className="block text-xs font-medium text-gray-600 mb-1">
              Hourly fee — trial ($) <span className="text-red-500">*</span>
            </label>
            <input id={fid('witness-rate-trial')} type="number" step="0.01" min="0"
              value={form.hourly_rate_trial}
              onChange={e => setForm({ ...form, hourly_rate_trial: e.target.value })}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errCls('hourly_rate_trial')}`} />
            {fieldErrors.hourly_rate_trial && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.hourly_rate_trial}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label htmlFor={fid('witness-retention-date')} className="block text-xs font-medium text-gray-600 mb-1">
              Retention date
            </label>
            <input id={fid('witness-retention-date')} type="date"
              value={form.retention_date}
              onChange={e => setForm({ ...form, retention_date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
        </div>
      )}

      {isRetained && (
        <div>
          <label htmlFor={fid('witness-prior-designated')} className="block text-xs font-medium text-gray-600 mb-1">
            Previously designated in other matters (last 4 years) <span className="text-red-500">*</span>
          </label>
          <textarea id={fid('witness-prior-designated')} rows={3}
            value={form.previously_designated_matters == null ? '' : form.previously_designated_matters}
            onChange={e => setForm({ ...form, previously_designated_matters: e.target.value })}
            placeholder="Case captions or 'None' — CCP §2034.415 disclosure obligation.  Empty text is a valid 'none' answer; save the form to record that."
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${errCls('previously_designated_matters')}`} />
          <p className="text-[11px] text-gray-500 mt-1">
            An empty text field is treated as an affirmative "none" answer once you save.
            Leave truly blank (unsaved) if you're still collecting the list.
          </p>
          {fieldErrors.previously_designated_matters && (
            <p className="text-xs text-red-600 mt-1">{fieldErrors.previously_designated_matters}</p>
          )}
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// ReclassifyWitnessModal — dedicated supersession flow.  Not chained
// useAsks (panel HIGH-3 rejected).  One atomic modal with:
//   1. Prior classification chip → target-type dropdown
//   2. §2034.270 statutory context box
//   3. Supersede-only branch (Ship 7i.1 backend supports supersede;
//      coexist requires partial-UNIQUE (matter_id, contact_id, witness_type)
//      which is Ship 7i.2 scope — see design contract note in file header)
//   4. Full expert-field form (if target is expert type) with per-field
//      completeness validation BEFORE the confirm gate (panel HIGH-2)
//   5. Required reclass_reason textarea
//   6. Confirm button disabled until both fields filled + completeness OK
//
// On 409 stale-record → close modal, refresh parent list, banner.
// ═══════════════════════════════════════════════════════════════════════
function ReclassifyWitnessModal ({
  matterId, witness, onClose, onReclassified, onStaleRecord, ask, fid,
}) {
  // Seed form from existing witness — preserves fields that survive a
  // reclassification (contact, phone, email, address, will_call).
  const [form, setForm] = useState(() => {
    const base = witnessToForm(witness)
    // Default target type: bump non-expert → expert_non_retained; expert
    // → fact.  Attorney can pick anything from the dropdown.
    const priorType = witness?.witness_type || 'fact'
    const suggested = isExpertType(priorType) ? 'fact' : 'expert_non_retained'
    return { ...base, witness_type: suggested }
  })
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})

  const priorType = witness?.witness_type || 'fact'
  const newType = form.witness_type
  const isExpert = isExpertType(newType)
  const isRetained = isRetainedExpertType(newType)
  const typeUnchanged = newType === priorType

  const submit = async () => {
    setErr(''); setFieldErrors({})
    if (typeUnchanged) {
      setErr('Pick a DIFFERENT target classification — reclassification is only for a type change.  Use Edit for same-type field updates.')
      return
    }
    if (!reason.trim()) {
      setErr('A written reason is required.  It is stored on the supersession record so a later reviewer can see why the designation changed.')
      return
    }

    // Panel HIGH-2 — client-side gate BEFORE the confirm ceremony.
    const check = validateCompletenessForType({
      witness_type: newType,
      expert_specialty: form.expert_specialty,
      qualifications_summary: form.qualifications_summary,
      subject_of_testimony: form.subject_of_testimony,
      hourly_rate_deposition: form.hourly_rate_deposition,
      hourly_rate_trial: form.hourly_rate_trial,
      previously_designated_matters: form.previously_designated_matters,
    })
    if (!check.ok) {
      const map = {}
      for (const m of check.missing) map[m.field] = `${m.message} (${m.statute})`
      setFieldErrors(map)
      setErr(`Cannot reclassify to ${witnessTypeMeta(newType).label} until required fields are filled — see red highlights below.`)
      return
    }

    // Confirm ceremony — final gate before the network call.  useAsk
    // is a single atomic confirm here (panel HIGH-3: NOT a chained
    // decision tree).  The radio + reason live in the modal already.
    const confirmed = await ask({
      type: 'confirm',
      title: 'Confirm §2034.270 supersession',
      message:
        `Reclassify "${displayWitnessName(witness)}" from ${witnessTypeMeta(priorType).label} to ${witnessTypeMeta(newType).label}.\n\n` +
        'This is a SUPERSESSION event — the prior classification row stays in the audit trail marked SUPERSEDED, and a NEW row is created as the current designation.  ' +
        'Under CCP §2034.270 you may have supplemental-designation timing implications; verify your service deadline before proceeding.\n\n' +
        `Reason:\n${reason.trim()}`,
      confirmLabel: 'Record supersession',
      cancelLabel: 'Cancel',
      danger: true,
    })
    if (!confirmed) return

    setBusy(true)
    try {
      // Ship 7i.2 CB-4 — identity is carried forward from the PRIOR row, never
      // re-derived from the form.  A supersession is the same witness under a
      // new classification, and the backend's chain-integrity trigger refuses
      // any successor whose contact_id differs from its parent's.  Ad-hoc
      // witnesses (contact_id NULL) carry display_name forward instead.
      const isAdHocWitness = witness.contact_id == null
      const payload = formToPayload(form, isAdHocWitness ? 'ad_hoc' : 'existing')
      // Backend contract: POST /api/matter-witnesses with
      //   { matter_id, contact_id, witness_type, ...fields,
      //     reclassify_from_witness_id, confirm_supersession: true,
      //     base_updated_at }
      // The backend runs SELECT FOR UPDATE + optimistic lock + atomic
      // supersession + audit.  409 outcomes are surfaced to the user.
      await createMatterWitness({
        matter_id: matterId,
        ...payload,
        // Preserve identity on supersession — the successor must belong to
        // the same (matter, contact) or the chain-integrity trigger refuses.
        // XOR: exactly one of contact_id / display_name.
        ...(isAdHocWitness
          ? { contact_id: null, display_name: witness.display_name }
          : { contact_id: witness.contact_id }),
        reclassify_from_witness_id: witness.id,
        confirm_supersession: true,
        // Second, distinct acknowledgement the server requires only when the
        // row being superseded was WITHDRAWN.  Superseding a withdrawn row is
        // the supported way to designate that person again — the withdrawal
        // stays on the record as the chain tail — but the server refuses with
        // 409 prior_row_withdrawn until the caller says so explicitly, so that
        // a plain reclassification cannot resurrect a pulled expert by
        // accident.  Sent only when it applies; harmless otherwise, but kept
        // conditional so the flag means what it says.
        ...(witness.withdrawn_at != null ? { confirm_redesignate_withdrawn: true } : {}),
        base_updated_at: witness.updated_at || undefined,
        // The reason the attorney typed above.  This modal required it,
        // disabled its submit button without it, and echoed it back in the
        // confirm dialog — and then omitted it here, so it reached no
        // persistence layer at all.  It is now stored on the successor row
        // (matter_witnesses.reclass_reason), written in the same INSERT that
        // creates the supersession record.
        reclass_reason: reason.trim(),
        notes: form.notes || (witness.notes || null),
      })
      onReclassified()
    } catch (e) {
      const status = e?.response?.status
      const code = e?.response?.data?.error
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Could not record supersession'
      if (status === 400 && e?.response?.data?.missing_fields) {
        setFieldErrors(parsePerFieldErrors(e))
        setErr('Missing required fields — see red highlights below.')
      } else if (status === 409 && (code === 'supersession_target_stale' || code === 'base_stale' || code === 'active_row_conflict' || code === 'supersession_race_lost' || code === 'prior_row_withdrawn')) {
        // Panel Frontend HIGH-1: refresh parent + banner, close modal.
        onStaleRecord?.(msg)
        onClose()
      } else {
        setErr(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  const { containerProps } = useModal({ onClose, onSubmit: submit })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div {...containerProps} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Change witness classification</h3>
            <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${witnessTypeMeta(priorType).chip}`}>
                {witnessTypeMeta(priorType).label}
              </span>
              <span>→</span>
              <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${witnessTypeMeta(newType).chip}`}>
                {witnessTypeMeta(newType).label}
              </span>
              <span className="text-gray-400">for {displayWitnessName(witness)}</span>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 space-y-5">
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3 whitespace-pre-wrap">
              {err}
            </div>
          )}

          {/* Statutory context */}
          <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
            <div className="font-semibold mb-1">⚠ §2034.270 supersession event</div>
            <p>
              Changing a witness's classification after initial disclosure creates a
              supplemental-designation obligation.  The prior row stays in the audit
              trail marked SUPERSEDED (§2034.300 evidentiary record); a new row is
              created as the current designation and served to opposing counsel.
              Verify your service deadline before recording this change.
            </p>
          </div>

          {/* Target-type picker */}
          <div>
            <label htmlFor={fid('reclass-target-type')} className="block text-xs font-medium text-gray-600 mb-1">
              New classification <span className="text-red-500">*</span>
            </label>
            <select
              id={fid('reclass-target-type')}
              value={newType}
              onChange={e => setForm({ ...form, witness_type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {WITNESS_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}{t.value === priorType ? ' (current)' : ''}</option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">
              {witnessTypeMeta(newType).desc}
            </p>
          </div>

          {/* Subject-of-testimony — required for both expert types.
              Rendered here (not below) because it also applies to any
              non-expert-to-expert transition. */}
          {isExpert && (
            <div>
              <label htmlFor={fid('reclass-subject')} className="block text-xs font-medium text-gray-600 mb-1">
                Subject of testimony <span className="text-red-500">*</span>
              </label>
              <textarea id={fid('reclass-subject')} rows={3}
                value={form.subject_of_testimony}
                onChange={e => setForm({ ...form, subject_of_testimony: e.target.value })}
                placeholder="CCP §2034.260(c)(2) — general substance of the testimony expected."
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  fieldErrors.subject_of_testimony ? 'border-red-500' : 'border-gray-300'
                }`} />
              {fieldErrors.subject_of_testimony && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.subject_of_testimony}</p>
              )}
            </div>
          )}

          {isExpert && (
            <ExpertFieldsSection
              form={form} setForm={setForm}
              fieldErrors={fieldErrors}
              isRetained={isRetained}
              fid={fid}
            />
          )}

          {/* Reason capture — REQUIRED per design contract */}
          <div>
            <label htmlFor={fid('reclass-reason')} className="block text-xs font-medium text-gray-600 mb-1">
              Reason for reclassification <span className="text-red-500">*</span>
            </label>
            <textarea id={fid('reclass-reason')} rows={4}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Concrete facts an auditor would need — e.g. 'Deposition testimony on 2026-06-15 revealed Dr. Smith formed opinions requiring expert designation.'  Stored on the supersession record."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>

          <p className="text-[11px] text-gray-500 italic">
            Cal. Code Civ. Proc. §2034.270:  A party who has designated an expert
            witness has a continuing duty to supplement the designation if the
            expert's opinions materially change.  Ship 7i.1 records the change as
            an atomic supersession — the prior row is preserved for §2034.300
            evidentiary purposes.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={submit}
            disabled={busy || typeUnchanged || !reason.trim()}
            className="px-4 py-2 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50">
            {busy ? 'Recording…' : 'Record supersession'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// RecordDisclosureModal — append-only §2034.270 designation-round event.
// Backend endpoint POST /api/matter-witnesses/:id/disclosure-events.
// served_by is ALWAYS set from request.user.id on the backend (CRIT-4
// IDOR fix); the frontend does not send it.
// ═══════════════════════════════════════════════════════════════════════
function RecordDisclosureModal ({ witness, onClose, onSaved, fid }) {
  const [designationRound, setDesignationRound] = useState('initial')
  const [servedAt, setServedAt] = useState(() => new Date().toISOString().slice(0, 10))
  const [serviceMethod, setServiceMethod] = useState('mail')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const submit = async () => {
    setErr('')
    if (!servedAt) { setErr('Enter the date the designation was served.'); return }
    setBusy(true)
    try {
      // Send served_at as ISO with T00:00:00Z suffix so the backend
      // parses the date at UTC midnight and the same date is stored
      // regardless of the viewer's TZ.
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(servedAt)
        ? `${servedAt}T00:00:00Z`
        : servedAt
      await createWitnessDisclosureEvent(witness.id, {
        designation_round: designationRound,
        served_at: iso,
        service_method: serviceMethod || null,
        notes: notes.trim() || null,
      })
      onSaved()
    } catch (e) {
      const code = e?.response?.data?.error
      const msg = e?.response?.data?.message || e?.response?.data?.error || 'Could not record disclosure event'
      if (code === 'duplicate_disclosure_event') {
        setErr(`${msg}  Change the round or the served-at date.`)
      } else {
        setErr(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  const { containerProps } = useModal({ onClose, onSubmit: submit })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div {...containerProps} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Record Disclosure Event</h3>
            <div className="text-xs text-gray-500 mt-0.5">for {displayWitnessName(witness)}</div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>
          )}
          <div>
            <label htmlFor={fid('disclose-round')} className="block text-xs font-medium text-gray-600 mb-1">
              Designation round <span className="text-red-500">*</span>
            </label>
            <select id={fid('disclose-round')} value={designationRound}
              onChange={e => setDesignationRound(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
              {DESIGNATION_ROUNDS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor={fid('disclose-served-at')} className="block text-xs font-medium text-gray-600 mb-1">
                Served on <span className="text-red-500">*</span>
              </label>
              <input id={fid('disclose-served-at')} type="date"
                value={servedAt}
                onChange={e => setServedAt(e.target.value)}
                data-autofocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label htmlFor={fid('disclose-method')} className="block text-xs font-medium text-gray-600 mb-1">
                Service method
              </label>
              <select id={fid('disclose-method')} value={serviceMethod}
                onChange={e => setServiceMethod(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
                {SERVICE_METHODS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor={fid('disclose-notes')} className="block text-xs font-medium text-gray-600 mb-1">
              Notes
            </label>
            <textarea id={fid('disclose-notes')} rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional — e.g. 'Served with initial expert-witness designation and demand for exchange.  Certificate of service in matter file.'"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>
          <p className="text-[11px] text-gray-500 italic">
            Disclosure events are APPEND-ONLY per §2034.300 evidentiary
            protection.  You cannot edit or delete an event once recorded —
            record a new event if you need to correct a served-at date.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy}
            className="px-4 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
            {busy ? 'Recording…' : 'Record disclosure event'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// DisclosureEventsPanel — inline read-only list per witness.  Fetches
// events on first mount + refetches when parent bumps a version key.
// ═══════════════════════════════════════════════════════════════════════
function DisclosureEventsPanel ({ witnessId }) {
  const [events, setEvents] = useState(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await listWitnessDisclosureEvents(witnessId)
        if (!cancelled) setEvents(Array.isArray(r?.data) ? r.data : [])
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.error || 'Could not load disclosure events')
      }
    })()
    return () => { cancelled = true }
  }, [witnessId])

  if (err) return <div className="text-xs text-red-700 bg-red-50 rounded p-2">{err}</div>
  if (events == null) return <div className="text-xs text-gray-400 italic">Loading…</div>
  if (events.length === 0) return (
    <div className="text-xs text-gray-500 italic pl-4">
      No disclosure events recorded yet.  Click <strong>Record Disclosure</strong>
      on the row above to add one.
    </div>
  )

  return (
    <div className="ml-4 border-l-2 border-emerald-300 pl-4 space-y-2">
      <div className="text-xs font-semibold text-emerald-800">📜 Disclosure history</div>
      {/* Six columns of served names and addresses: the table scrolls in
          its own box.  Today the panel sits in a row of the roster table,
          whose overflow-x-auto box already scrolls it; this keeps it from
          being cut off if it is ever shown inside a box that clips. */}
      <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-gray-500">
          <tr>
            <th className="pr-3 pb-1 font-normal">Round</th>
            <th className="pr-3 pb-1 font-normal">Served on</th>
            <th className="pr-3 pb-1 font-normal">Method</th>
            <th className="pr-3 pb-1 font-normal">Served by</th>
            <th className="pr-3 pb-1 font-normal">Served as (frozen)</th>
            <th className="pr-3 pb-1 font-normal">Notes</th>
          </tr>
        </thead>
        <tbody className="text-gray-700">
          {events.map(ev => {
            const round = DESIGNATION_ROUNDS.find(r => r.value === ev.designation_round)
            const method = SERVICE_METHODS.find(m => m.value === ev.service_method)
            const servedAt = ev.served_at ? new Date(ev.served_at).toLocaleDateString('en-CA') : '—'
            // Ship 7i.2 CB-4b — the frozen point-in-time snapshot.  A later
            // edit to the witness override OR the contact card cannot rewrite
            // this, so it answers "what address did we actually serve?".
            // NULL means the event predates Ship 7i.2 — provenance unknown,
            // and we say so rather than showing current data as if it were
            // the served data.
            const hasSnapshot = ev.served_name != null || ev.served_address != null
            return (
              <tr key={ev.id} className="border-t border-emerald-100">
                <td className="pr-3 py-1">{round?.label || ev.designation_round}</td>
                <td className="pr-3 py-1">{servedAt}</td>
                <td className="pr-3 py-1">{method?.label || ev.service_method || '—'}</td>
                <td className="pr-3 py-1">{ev.served_by_name || '—'}</td>
                <td className="pr-3 py-1">
                  {hasSnapshot ? (
                    <>
                      {ev.served_name && <div className="font-medium">{ev.served_name}</div>}
                      {ev.served_address
                        ? <div className="text-gray-600">📍 {ev.served_address}</div>
                        : <div className="text-amber-700 italic">no address served</div>}
                      {(ev.served_address_source === 'override' || ev.served_name_source === 'override') && (
                        <div className="text-[10px] text-purple-700">used per-witness override</div>
                      )}
                    </>
                  ) : (
                    <span
                      className="text-amber-700 italic"
                      title="This event was recorded before service details were snapshotted. The witness and contact records may have changed since; we cannot certify what was served."
                    >
                      ⚠ not recorded
                    </span>
                  )}
                </td>
                <td className="pr-3 py-1 whitespace-pre-wrap">{ev.notes || '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
      <p className="text-[11px] text-gray-500">
        “Served as” is frozen at the moment of service — later edits to the witness
        or contact record do not change it.
      </p>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
// InlineWitnessContactCreator — create Contact + link to matter, hand
// back to the parent modal so it can select the new contact_id.
// Preserved from Ship 7i.
// ═══════════════════════════════════════════════════════════════════════
function InlineWitnessContactCreator ({ matterId, suggestedGroup = 'witness', onCreated }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', contact_group: suggestedGroup,
    email: '', phone: '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const submit = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) {
      setErr('Provide at least a first or last name'); return
    }
    setBusy(true); setErr('')
    try {
      const r = await api.post('/api/contacts', {
        first_name: form.first_name.trim() || null,
        last_name:  form.last_name.trim()  || null,
        contact_group: form.contact_group,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      })
      const newContact = r.data
      try {
        await api.post(`/api/contacts/matter/${matterId}`, {
          contact_id: newContact.id,
          // matter_contacts.role is free text rendered to humans — send the
          // label ('Witness'), while contact_group carries the enum value.
          role: groupLabel(form.contact_group),
        })
      } catch { /* already linked — carry on */ }
      await onCreated(newContact)
    } catch (e) {
      setErr(e?.response?.data?.error || 'Could not create contact')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 space-y-2 text-xs">
      <div className="font-semibold text-purple-800">
        + New Contact for this matter (auto-linked)
      </div>
      {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-1">{err}</div>}
      <div className="grid grid-cols-2 gap-2">
        <input type="text" placeholder="First name" value={form.first_name}
          onChange={e => setForm({ ...form, first_name: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1" />
        <input type="text" placeholder="Last name" value={form.last_name}
          onChange={e => setForm({ ...form, last_name: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <select value={form.contact_group}
          onChange={e => setForm({ ...form, contact_group: e.target.value })}
          className="border border-gray-300 rounded px-1 py-1">
          {CONTACT_GROUPS.map(g =>
            <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
        <input type="email" placeholder="Email (opt)" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1" />
        <input type="tel" placeholder="Phone (opt)" value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          className="border border-gray-300 rounded px-2 py-1" />
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={submit} disabled={busy}
          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded disabled:opacity-50">
          {busy ? 'Creating…' : 'Create + link + select'}
        </button>
      </div>
    </div>
  )
}
